import React from "react";
import ReactDOM from "react-dom";
import Layout from "./components/Layout/Layout";
import Context from "./Context";
import config from "./config";
import { api, ws, cart, fav } from "./api";
import user from "./user";
import i18n from "./i18n";
import toastr from "../plugins/toastr";
import { say, ask, prompt, promptPassword } from "../plugins/dialogs";
import { essenceClass } from "./essense";
import { notifier } from "./notifier";
import ObjectInspector from "react-object-inspector";
import ErrorBoundary from "./components/Layout/ErrorBoundary";
import Offline from "./components/Misc/Offline";

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};

    this.idleTimer = null;

    //Begin context object
    this.cx = {
      config, //attach config.js
      api, //attach api.js
      ws: new ws(),
      cart: new cart(),
      fav: new fav(),
      essenceClass, //attach  essence.js

      //Notifications & buzz
      notifierHasUpdated: () => {
        this.forceUpdate();
      },
      notifier: new notifier(() => this.cx.notifierHasUpdated(), false),

      //make key
      key: () =>
        `f${(+new Date()).toString(16)}${this.cx.getRandomIntInclusive(
          1000,
          9999
        )}`,
      getRandomIntInclusive: (min, max) => {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1)) + min;
      },

      //User placeholder
      user: user,

      //Types Mapping
      getType: (typecode) => user.typeMap[typecode] || false,

      //Internationalization handling
      i18n,
      t: (expr) => this.cx.i18n.t(expr),

      //Toastr messages and Swal dialogs handling
      toast: (message, type = "info") => toastr[type](message),
      ask: (...params) => ask(...params),
      say: (...params) => say(...params),
      prompt: (...params) => prompt(...params),
      promptPassword: (...params) => promptPassword(...params),

      //Breadcrumbs, Actions area & Title handling
      breadCrumbs: [],
      breadCrumbsAdd: (title, url) => {
        this.cx.breadCrumbs.push({ title, url });
      },
      breadCrumbsFlush: () => {
        this.cx.breadCrumbs.length = 0;
      },
      browserTitleSet: (pageTitle = "") => {
        const browserTitle = `${config.titlePrefix} ${
          pageTitle ? ":" : ""
        } ${pageTitle}`;
        document.title = browserTitle;
      },

      titleAreaRef: React.createRef(),
      titleAreaRivalRef: React.createRef(),
      titleAreaSet: (items) => {
        if (this.cx.titleAreaRivalRef.current) {
          this.cx.titleAreaRivalRef.current.classList.add("invisible");
        }
        return this.cx.titleAreaRef.current
          ? ReactDOM.render(items, this.cx.titleAreaRef.current)
          : false;
      },
      titleAreaFlush: () => {
        if (this.cx.titleAreaRivalRef.current) {
          this.cx.titleAreaRivalRef.current.classList.remove("invisible");
        }
        return this.cx.titleAreaRef.current
          ? ReactDOM.render(<div></div>, this.cx.titleAreaRef.current)
          : false;
      },

      actionsRef: React.createRef(),
      actionsSet: (items) =>
        this.cx.actionsRef.current
          ? ReactDOM.render(items, this.cx.actionsRef.current)
          : false,
      actionsFlush: () =>
        this.cx.actionsRef.current
          ? ReactDOM.render(<div></div>, this.cx.actionsRef.current)
          : false,

      //Force update
      force: () => {
        this.updateStateFromCX();
      },

      //Aside handling
      asideIsExpanded: false,
      asideIsHovered: false,
      asideIsOn: false,
      asideToggle: () => {
        this.cx.asideIsExpanded = !this.cx.asideIsExpanded;
        this.updateStateFromCX();
      },
      asideMouseEnter: () => {
        this.cx.asideIsHovered = true;
        this.updateStateFromCX();
      },
      asideMouseLeave: () => {
        this.cx.asideIsHovered = false;
        this.updateStateFromCX();
      },
      asideToggleOnOff: (value = !this.cx.asideIsOn) => {
        this.cx.asideIsOn = value;
        this.updateStateFromCX();
      },

      //UserMenu handling
      userMenuIsExpanded: false,
      userMenuToggle: () => {
        this.cx.userMenuIsExpanded = !this.cx.userMenuIsExpanded;
        this.updateStateFromCX();
      },

      //Handle if some modal is opened
      modalOpened: false,
      modalOpenedSet: (v) => {
        this.cx.modalOpened = v;
        this.updateStateFromCX();
      },

      //Force router redirect
      redirect: (url) => {
        this.props.history.push(url);
      },
      replace: (url) => {
        this.props.history.replace(url);
      },

      //Screen size and type
      screenHeight: null,
      screenAreaHeight: null,
      screenWidth: null,
      isMobile: false,

      //inspect
      inspect: (v) => (
        <ObjectInspector data={v} initialExpandedPaths={["root", "root.*"]} />
      ),
    };

    user.loadUser();
    user.onAfterLogin = () => {
      if (this.cx.user.isAuthorized) {
        this.cx.notifier.load();
        this.cx.cart.load();
        this.cx.fav.load();
      }
      this.updateStateFromCX();
    };
  }

  updateStateFromCX() {
    this.forceUpdate();
  }

  redirect(url) {
    const { history } = this.props;
    history.push(url, "");
    this.forceUpdate();
  }
  componentDidMount() {
    const { history } = this.props;
    this.unsubscribeFromHistory = history.listen(this.handleLocationChange);
    this.handleLocationChange(history.location);

    //Window size handler
    window.addEventListener("resize", this.handleWindowResize);
    this.handleWindowResize();
  }
  componentWillUnmount() {
    if (this.unsubscribeFromHistory) this.unsubscribeFromHistory();
    window.removeEventListener("resize", this.handleWindowResize);
  }
  handleLocationChange = (location) => {
    this.cx.actionsFlush();
    this.cx.titleAreaFlush();
    this.updateStateFromCX();
    this.cx.api.token.parse();
  };

  handleOnAction = (event) => {
    if (this.cx.api.token) {
      if (this.cx.api.token.goingToExpire()) {
        this.cx.api.token.exchange();
      }
    }
  };

  handleWindowResize = () => {
    this.cx.screenWidth = window.innerWidth;
    this.cx.screenHeight = window.innerHeight;
    this.cx.screenAreaHeight = window.innerHeight - 180;
    this.cx.isMobile = this.cx.screenWidth <= 991 ? true : false;
    this.forceUpdate();
  };

  render() {
    return (
      <>
        <ErrorBoundary>
          <Context.Provider value={this.cx}>
            <Offline />
            <Layout />
          </Context.Provider>
        </ErrorBoundary>
      </>
    );
  }
}

export default App;
